import { Checkbox as CompassCheckbox } from "@circleco/compass/components/Checkbox";
import { omit } from "lodash";
import type { ChangeEvent, ComponentPropsWithRef } from "react";

import type {
  CheckboxItemProps,
  Option,
} from "@/react/components/shared/uikit/Form/CheckboxGroup/CheckboxItem";
import { CheckboxItem } from "@/react/components/shared/uikit/Form/CheckboxGroup/CheckboxItem";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

// eslint-disable-next-line no-barrel-files/no-barrel-files -- This is a compatibility layer that re-exports types for SettingsApp
export type { CheckboxItemProps, Option };

type CompassCheckboxProps = ComponentPropsWithRef<typeof CompassCheckbox> & {
  "data-testid"?: string;
};

export function Checkbox(props: Readonly<CheckboxItemProps>) {
  const isCircleV4 = useIsCircleV4FeatureEnabled(
    "circle_v4_form_elements_enabled",
  );

  if (!isCircleV4) {
    return <CheckboxItem {...omit(props, ["layout"])} />;
  }

  const {
    option,
    checked: isChecked,
    onChange,
    name,
    disabled: isDisabled = false,
    error,
    invalid: isInvalid = false,
    isDescriptionTooltip = false,
    uncheckDisabled: isUncheckDisabled = false,
    indeterminate: isIndeterminate = false,
    dataTestId,
    id,
    required: isRequired,
    className,
    layout,
  } = props;

  const optionValue = option.value?.toString() ?? "";

  const handleCheckedChange = (newChecked: boolean | "indeterminate") => {
    const isNewChecked = newChecked === "indeterminate" ? true : newChecked;
    if (!isNewChecked && isUncheckDisabled) return;

    const syntheticEvent = {
      target: {
        checked: isNewChecked,
        name: name ?? "",
        type: "checkbox",
        value: optionValue,
      },
      currentTarget: {
        checked: isNewChecked,
        name: name ?? "",
        type: "checkbox",
        value: optionValue,
      },
    } as ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
  };

  const compassLayout = layout === "horizontal" ? "horizontal" : undefined;

  const compassProps: CompassCheckboxProps = {
    id,
    label: option.richLabel || option.label,
    description: isDescriptionTooltip ? undefined : option.description,
    error,
    invalid: isInvalid,
    value: optionValue,
    checked: isChecked,
    indeterminate: isIndeterminate,
    onCheckedChange: handleCheckedChange,
    name,
    disabled: isDisabled,
    required: isRequired,
    "data-testid": dataTestId,
    ...(compassLayout != null ? { layout: compassLayout } : {}),
  };

  return (
    <div className={className}>
      <CompassCheckbox {...compassProps} />
    </div>
  );
}

// eslint-disable-next-line no-barrel-files/no-barrel-files -- Compatibility alias for CheckboxItem consumers
export { Checkbox as CheckboxItem };
